import jsYaml from 'js-yaml';

/**
 * parseSpec(input)
 * Accepts either a JSON string or YAML string.
 * Validates the result is an object with an `openapi` or `swagger` key.
 * Returns a normalized spec object with: info, servers, paths, components.
 * Throws descriptive errors for invalid input.
 */
export function parseSpec(input) {
  if (!input || typeof input !== 'string' || input.trim() === '') {
    throw new Error('Input is empty. Please paste or upload an OpenAPI/Swagger specification.');
  }

  let raw;

  // Try JSON first
  if (input.trim().startsWith('{') || input.trim().startsWith('[')) {
    try {
      raw = JSON.parse(input);
    } catch (e) {
      throw new Error(`Invalid JSON: ${e.message}`);
    }
  } else {
    // Try YAML
    try {
      raw = jsYaml.load(input);
    } catch (e) {
      throw new Error(`Invalid YAML: ${e.message}`);
    }
  }

  if (!raw || typeof raw !== 'object') {
    throw new Error('Parsed result is not a valid object. Check your specification format.');
  }

  if (!raw.openapi && !raw.swagger) {
    throw new Error('Missing required field: spec must have an "openapi" (3.x) or "swagger" (2.x) key at the root level.');
  }

  const isV3 = !!raw.openapi;

  // Normalize info
  const info = {
    title: raw.info?.title || 'Untitled API',
    version: raw.info?.version || '0.0.1',
    description: raw.info?.description || '',
    contact: raw.info?.contact || null,
    license: raw.info?.license || null,
    termsOfService: raw.info?.termsOfService || null,
  };

  // Normalize servers
  let servers = [];
  if (isV3 && Array.isArray(raw.servers)) {
    servers = raw.servers.map(s => ({
      url: s.url || '',
      description: s.description || '',
    }));
  } else if (!isV3) {
    // Swagger 2.0
    const scheme = (raw.schemes && raw.schemes[0]) || 'https';
    const host = raw.host || 'localhost';
    const basePath = raw.basePath || '/';
    servers = [{ url: `${scheme}://${host}${basePath}`, description: 'Default server' }];
  }

  if (servers.length === 0) {
    servers = [{ url: '', description: 'No server specified' }];
  }

  // Normalize paths
  const paths = raw.paths || {};

  // Components / definitions for schema refs
  const components = isV3
    ? (raw.components || {})
    : { schemas: raw.definitions || {} };

  return {
    raw,
    info,
    servers,
    paths,
    components,
    isV3,
    specVersion: raw.openapi || raw.swagger,
    tags: raw.tags || [],
  };
}

export function resolveRef(ref, spec) {
  if (!ref || !ref.startsWith('#/')) return null;
  const parts = ref.replace('#/', '').split('/');
  let current = spec.raw;
  for (const part of parts) {
    if (!current) return null;
    current = current[part.replace(/~1/g, '/').replace(/~0/g, '~')];
  }
  return current;
}

export function getMethodColor(method) {
  const m = method?.toLowerCase();
  const map = {
    get:     'var(--get)',
    post:    'var(--post)',
    put:     'var(--put)',
    patch:   'var(--patch)',
    delete:  'var(--delete)',
    head:    'var(--head)',
    options: 'var(--options)',
  };
  return map[m] || 'var(--text-dim)';
}

export function getMethodBg(method) {
  const m = method?.toLowerCase();
  const map = {
    get:     'rgba(232,255,90,0.08)',
    post:    'rgba(79,255,176,0.08)',
    put:     'rgba(90,180,255,0.08)',
    patch:   'rgba(255,170,90,0.08)',
    delete:  'rgba(255,107,107,0.08)',
    head:    'rgba(192,132,252,0.08)',
    options: 'rgba(136,136,128,0.08)',
  };
  return map[m] || 'rgba(136,136,128,0.08)';
}

export function schemaToExampleJson(schema, components, depth = 0) {
  if (!schema || depth > 4) return null;

  if (schema.$ref) {
    const parts = schema.$ref.replace('#/', '').split('/');
    let resolved = components._raw || {};
    // Try to resolve from raw spec
    for (const p of parts) resolved = resolved?.[p];
    if (!resolved) return `"<${schema.$ref}>"`;
    return schemaToExampleJson(resolved, components, depth + 1);
  }

  if (schema.example !== undefined) return schema.example;

  const type = schema.type || (schema.properties ? 'object' : null);

  if (type === 'object' || schema.properties) {
    const obj = {};
    const props = schema.properties || {};
    for (const [key, val] of Object.entries(props)) {
      obj[key] = schemaToExampleJson(val, components, depth + 1);
    }
    return obj;
  }

  if (type === 'array') {
    const item = schemaToExampleJson(schema.items, components, depth + 1);
    return item !== null ? [item] : [];
  }

  if (schema.enum) return schema.enum[0];

  const defaults = {
    string:  'string',
    integer: 0,
    number:  0.0,
    boolean: true,
  };
  return defaults[type] ?? null;
}
